// The four things a user does to a nugget (TODO §17.4 / §17.6), as functions the UI calls.
//
// They live out of the components because each one is the same shape of job — copy some
// bodies between owner keys, write a record, dispatch a reducer or two — and because the
// order within each one matters more than the click that starts it.
//
// Every one of them is deliberately NOT a thunk. They take the dispatch and the state they
// need as arguments, which keeps them callable from the panel, from the slide chrome and
// (later, §17.4.7) from an assistant tool, without any of the three importing the store.

import {
  remapSlideContents,
  slideContentRefs,
  type ContentRefKind,
  type RemapRef,
} from "@/lib/contentRefs";
import {
  createNugget,
  deleteNuggetCascade,
  getAsset,
  getNugget,
  loadNuggetBodies,
  putAsset,
  putDiagramDoc,
  putChartDoc,
  putGanttDoc,
  putGriddDoc,
} from "@/lib/idb";
import { materializeFrom, nuggetSlideFrom } from "@/lib/nugget";
import { deckOwner, nuggetOwner } from "@/lib/ownerKey";
import type { AppDispatch } from "@/store";
import { linkBroken, linkSet, updateSlide } from "@/store/documentSlice";
import { diagramDocsMerged } from "@/store/diagramSlice";
import { chartDocsMerged } from "@/store/chartSlice";
import { ganttDocsMerged } from "@/store/ganttSlice";
import { griddDocsMerged } from "@/store/griddSlice";
import type { DiagramState } from "@/store/diagramSlice";
import type { ChartState } from "@/store/chartSlice";
import type { GanttState } from "@/store/ganttSlice";
import type { GriddState } from "@/store/griddSlice";
import type { NuggetRecord } from "@/types/nugget";
import type { SlideContents, SlideData } from "@/types/document";

// === Copying bodies between owners ===
//
// Save as nugget and Break link are the same operation pointed in opposite directions: take
// every body a slide's windows reference, write it under the OTHER owner, and repoint the
// slide. So one function does both, and the two callers differ in exactly one thing — whether
// the ids are minted fresh.
//
// That single flag is the whole difference between a MOVE and a COPY, and it is worth being
// precise about, because `griddDocs`/`diagramDocs`/`ganttDocs`/`chartDocs`/`assets` are all
// keyed by the
// body's own id:
//
//   • `mintNewIds: false` (Save as nugget) rewrites the SAME row with a new owner. The body
//     moves from the deck to the nugget, the slide's references do not change at all, and
//     nothing is left behind — which is right, because that slide is about to BE the nugget's.
//   • `mintNewIds: true` (Break link) writes NEW rows under this deck and leaves the nugget's
//     originals untouched, because four other decks may still be rendering them.

// The three body maps as Redux holds them. Passed in rather than read from IndexedDB, and
// that is not a shortcut: the store holds the version the user is looking at, whereas the
// database holds whatever the last ~400ms debounce landed. Copying the stored one would
// silently drop the last half-second of typing out of the new nugget — and out of the copy
// Break link leaves behind, which is worse, because there the original is about to stop being
// consulted.
export interface LiveBodies {
  gridd: GriddState;
  diagram: DiagramState;
  gantt: GanttState;
  chart: ChartState;
}

async function copySlideBodies(
  slide: SlideData,
  bodies: LiveBodies,
  toOwner: string,
  mintNewIds: boolean,
): Promise<{ contents: SlideContents; copied: LiveBodies }> {
  const fresh = new Map<string, string>();
  const idFor = (id: string) => {
    if (!mintNewIds) return id;
    let next = fresh.get(id);
    if (next === undefined) {
      next = crypto.randomUUID();
      fresh.set(id, next);
    }
    return next;
  };

  const copied: LiveBodies = { gridd: {}, diagram: {}, gantt: {}, chart: {} };
  for (const ref of slideContentRefs(slide.contents)) {
    const newId = idFor(ref.id);
    if (ref.kind === "grid") {
      const doc = bodies.gridd[ref.id];
      if (!doc) continue;
      await putGriddDoc(toOwner, newId, doc);
      copied.gridd[newId] = doc;
    } else if (ref.kind === "diagram") {
      const doc = bodies.diagram[ref.id];
      if (!doc) continue;
      await putDiagramDoc(toOwner, newId, doc);
      copied.diagram[newId] = doc;
      // §21/§4.4.2 The images INSIDE a scene are ordinary `assets` rows referenced by the
      // scene's own `fileIds`, not by any content window — so the walk above never sees
      // them. Left behind, the copy renders that diagram with holes. Their ids are
      // deliberately NOT minted fresh: an assetId is referenced from inside the element
      // data, so a new one would mean rewriting the scene. Same id, two rows, different
      // owners — which is all either lifetime needs.
      for (const fileId of doc.fileIds ?? []) {
        const asset = await getAsset(fileId);
        if (asset) await putAsset(fileId, toOwner, asset.blob, asset.mime);
      }
    } else if (ref.kind === "gantt") {
      const doc = bodies.gantt[ref.id];
      if (!doc) continue;
      await putGanttDoc(toOwner, newId, doc);
      copied.gantt[newId] = doc;
    } else if (ref.kind === "chart") {
      // §15/§9 The gantt branch again: one JSON record, no blobs hanging off it. A URL source
      // travels with the doc, which is right — a nugget that pulls a live table should keep
      // pulling it in whichever deck uses it — and its cached `rows` travel too, so a copy
      // renders before it ever refreshes.
      const doc = bodies.chart[ref.id];
      if (!doc) continue;
      await putChartDoc(toOwner, newId, doc);
      copied.chart[newId] = doc;
    } else {
      const asset = await getAsset(ref.id);
      if (asset) await putAsset(newId, toOwner, asset.blob, asset.mime);
    }
  }

  const remap: RemapRef = (_kind: ContentRefKind, id: string) => idFor(id);
  return { contents: remapSlideContents(slide.contents, remap), copied };
}

// Merge whatever was just copied into the store, under the ids the slide is about to point
// at. Skips empty maps so an ordinary text slide dispatches nothing at all.
function mergeBodies(dispatch: AppDispatch, copied: LiveBodies): void {
  if (Object.keys(copied.gridd).length > 0) {
    dispatch(griddDocsMerged(copied.gridd));
  }
  if (Object.keys(copied.diagram).length > 0) {
    dispatch(diagramDocsMerged(copied.diagram));
  }
  if (Object.keys(copied.gantt).length > 0) {
    dispatch(ganttDocsMerged(copied.gantt));
  }
  if (Object.keys(copied.chart).length > 0) {
    dispatch(chartDocsMerged(copied.chart));
  }
}

// §17.4.4 **Save slide as nugget.** Copy this slide's bodies and blobs to a new nugget owner
// key, write the nugget record, and link the source slide — it becomes the first user of the
// nugget it just created, which is what anyone would expect.
//
// The ids are kept, not minted: this slide is about to BE the nugget's slide, so repointing it
// at fresh copies would leave the deck's original bodies orphaned and the copy identical. The
// rows move owner; the references do not move at all.
export async function saveSlideAsNugget(
  dispatch: AppDispatch,
  slide: SlideData,
  bodies: LiveBodies,
): Promise<NuggetRecord | null> {
  const nuggetId = crypto.randomUUID();
  const record = await createNugget(nuggetId, nuggetSlideFrom(slide));
  if (!record) return null;
  // **Link BEFORE moving the bodies**, and the order is not arbitrary. Moving a row's owner
  // takes a transaction; a Gridd embed emits a save every ~400ms while someone is typing; and
  // that save resolves its owner from the slide (`ownerForContentDoc`). So with the bodies
  // moved first, a keystroke landing in the gap would resolve to the DECK — because the slide
  // is not linked yet — and file the nugget's body straight back under the deck. The nugget
  // would be left pointing at a body this deck's cascade delete can reach, which is the one
  // failure §17.1.3 exists to make impossible.
  //
  // Linking first inverts that: every concurrent write in the gap resolves to the nugget,
  // which is where the row is going anyway. If the move below then fails halfway, the next
  // edit to each remaining body re-files it correctly, because the slide is already linked.
  // One order is self-healing and the other corrupts; they are otherwise identical.
  dispatch(linkSet({ id: slide.id, link: { nuggetId, rev: record.rev } }));
  await copySlideBodies(slide, bodies, nuggetOwner(nuggetId), false);
  return record;
}

// §17.4.3 **Break link.** Mint fresh ids for this slide's grid bodies and image blobs, copy
// them from the nugget owner key to this deck's, repoint the contents, clear the link.
//
// That is per-slide `remapBundle`, which is why the id walk is the one extracted into
// `contentRefs.ts` rather than a second copy of it (§17.4.3).
//
// The order matters and it is the reverse of a delete's: the copies are written FIRST, while
// the slide still points at the originals, and only then is the slide repointed. Interrupted
// halfway, the worst outcome is a set of unreferenced rows under the deck — invisible, and
// nothing anybody was reading has moved.
//
// **Nothing is deleted.** The nugget's own bodies stay exactly where they are: they are still
// the nugget's, and four other decks may be rendering them. This is a copy-out, not a move.
export async function breakSlideLink(
  dispatch: AppDispatch,
  slide: SlideData,
  bodies: LiveBodies,
  deckId: string,
): Promise<void> {
  if (!slide.link) return;
  const { contents, copied } = await copySlideBodies(
    slide,
    bodies,
    deckOwner(deckId),
    true,
  );
  // The fresh bodies have to be in the store under their NEW ids before the slide points at
  // them, or the window renders empty for a frame and — worse — the Gridd embed remounts on a
  // docId whose body Redux has not got.
  mergeBodies(dispatch, copied);
  // ONE action for the repoint and the unlink — see `linkBroken`, which carries the two ways
  // splitting them corrupts somebody else's deck.
  dispatch(linkBroken({ id: slide.id, contents }));
}

// §17.4.5 **Insert a nugget as a new slide.** The caller has already added an empty slide
// (`addSlide`) — the same shape the Gridd Library pick uses, and for the same reason: a pick
// APPENDS a new slide, it never overwrites the selected one.
//
// The bodies are loaded and merged into the store under the nugget's OWN ids, unchanged. That
// aliasing is the feature (§17.1.5): this deck's slide and every other deck's point at one
// body, and an edit in any of them is an edit in all of them.
export async function materializeNuggetIntoSlide(
  dispatch: AppDispatch,
  slideId: string,
  nuggetId: string,
): Promise<NuggetRecord | null> {
  const record = await getNugget(nuggetId);
  if (!record) return null;
  const bodies = await loadNuggetBodies(nuggetId);
  // Bodies before contents, for the reason Break link writes its copies first: a content
  // window pointing at a body the store has not got yet is an empty frame at best.
  if (Object.keys(bodies.griddDocs).length > 0) {
    dispatch(griddDocsMerged(bodies.griddDocs));
  }
  if (Object.keys(bodies.diagramDocs).length > 0) {
    dispatch(diagramDocsMerged(bodies.diagramDocs));
  }
  if (Object.keys(bodies.ganttDocs).length > 0) {
    dispatch(ganttDocsMerged(bodies.ganttDocs));
  }
  if (Object.keys(bodies.chartDocs).length > 0) {
    dispatch(chartDocsMerged(bodies.chartDocs));
  }
  dispatch(updateSlide({ id: slideId, patch: materializeFrom(record) }));
  dispatch(linkSet({ id: slideId, link: { nuggetId, rev: record.rev } }));
  return record;
}

// §17.6.1 **Delete a nugget from the shelf.** Never touches a deck: decks keep the slide and
// drop the dead link on their next open (§17.2.5). The confirm dialog belongs to the caller,
// which is also what counts the decks — see `countDecksUsingNugget`.
export async function deleteNugget(nuggetId: string): Promise<void> {
  await deleteNuggetCascade(nuggetId);
}
